import React, { useState, useEffect } from 'react';
import { TrendingUp, Scale, X, Target, Users } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { JugadorDto, RegistroFisicoDto } from '../../types/jugador';
import ModalRegistroFisico from './ModalRegistroFisico';
import { useEstadisticas } from '../../hooks/useEstadisticas';
import { useJugadoresEquipoActivo } from '../../hooks/useJugadoresEquipoActivo';
import GraficaEvolucionFisica from '../charts/GraficaEvolucionFisica';
import { ModalFechasFaltas } from './ModalFechasFaltas';
import ModalMinutosJugador from './ModalMinutosJugador';

interface ModalDetalleJugadorProps {
  isOpen: boolean;
  onClose: () => void;
  jugador: JugadorDto | null;
  ultimoRegistroFisico?: RegistroFisicoDto | null;
  onRegistroFisicoActualizado?: () => void;
}

/**
 * Modal que muestra el detalle completo de un jugador con información personal,
 * estadísticas y registro físico
 */
const ModalDetalleJugador: React.FC<ModalDetalleJugadorProps> = ({
  isOpen,
  onClose,
  jugador,
  ultimoRegistroFisico,
  onRegistroFisicoActualizado
}) => {
  const [isModalRegistroFisicoOpen, setIsModalRegistroFisicoOpen] = useState(false);
  const [isModalFechasFaltasOpen, setIsModalFechasFaltasOpen] = useState(false);
  const [fechasFaltas, setFechasFaltas] = useState<string[]>([]);
  const { estadisticas, loading, error, obtenerEstadisticasCompletas, obtenerFechasFaltas } = useEstadisticas();
  const { temporada, equipo } = useJugadoresEquipoActivo();
  const [isModalMinutosOpen, setIsModalMinutosOpen] = useState(false);

  /**
   * Abre el modal de minutos por jornada
   */
  const handleAbrirModalMinutos = () => {
    setIsModalMinutosOpen(true);
  };

  /**
   * Cierra el modal de minutos por jornada
   */
  const handleCerrarModalMinutos = () => {
    setIsModalMinutosOpen(false);
  };

  // Cargar estadísticas cuando se abre el modal
  useEffect(() => {
    if (isOpen && jugador && temporada && equipo) {
      obtenerEstadisticasCompletas(jugador.id, temporada.id, equipo.nombre);
    }
  }, [isOpen, jugador, temporada, equipo, obtenerEstadisticasCompletas]);

  if (!jugador) return null;

  /**
   * Maneja la apertura del modal de registro físico
   */
  const handleAbrirRegistroFisico = () => {
    setIsModalRegistroFisicoOpen(true);
  };

  /**
   * Maneja la apertura del modal de fechas de faltas
   */
  const handleAbrirModalFechasFaltas = async () => {
    if (!jugador || !temporada) return;
    
    try {
      const fechas = await obtenerFechasFaltas(jugador.id, temporada.id);
      setFechasFaltas(fechas);
      setIsModalFechasFaltasOpen(true);
    } catch (error) {
      console.error('Error al obtener fechas de faltas:', error);
    }
  };

  /**
   * Maneja el cierre del modal de fechas de faltas
   */
  const handleCerrarModalFechasFaltas = () => {
    setIsModalFechasFaltasOpen(false);
    setFechasFaltas([]);
  };

  /**
   * Maneja el cierre del modal de registro físico
   */
  const handleCerrarRegistroFisico = () => {
    setIsModalRegistroFisicoOpen(false);
  };

  /**
   * Maneja cuando se actualiza un registro físico
   */
  const handleRegistroFisicoGuardado = () => {
    setIsModalRegistroFisicoOpen(false);
    onRegistroFisicoActualizado?.();
    // Recargar estadísticas para actualizar las gráficas
    obtenerEstadisticasCompletas(jugador.id);
  };

  /**
   * Formatea la fecha del registro físico
   */
  const formatearFecha = (fecha: string): string => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <div className="space-y-6">
          {/* Header del Modal */}
          <div className="flex items-center justify-between pb-4 border-b border-slate-700">
            <h2 className="text-2xl font-bold text-slate-100 font-poppins">
              {jugador.nombre} {jugador.apellido}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Sección 2: Estadísticas */}
          <Card variant="default" className="p-6">
            <div className="flex items-center mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-500 mr-2" />
              <h3 className="text-lg font-semibold text-slate-100 font-poppins">
                Estadísticas
              </h3>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                <p className="text-slate-400 font-poppins">Cargando estadísticas...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <TrendingUp className="w-16 h-16 text-slate-500 mb-4 mx-auto" />
                <p className="text-red-400 font-poppins text-lg mb-2">Error al cargar estadísticas</p>
                <p className="text-slate-500 font-poppins text-sm">{error}</p>
              </div>
            ) : estadisticas ? (
              <div className="space-y-6">
                {/* Estadísticas de Entrenamientos */}
                <div>
                  <h4 className="text-md font-semibold text-slate-200 font-poppins mb-3 flex items-center">
                    <Users className="w-4 h-4 text-emerald-500 mr-2" />
                    Entrenamientos
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-emerald-400 font-poppins">
                        {estadisticas.entrenamiento.totalDias}
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Total Días
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-400 font-poppins">
                        {estadisticas.entrenamiento.asistencias}
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Asistencias
                      </div>
                    </div>
                    <div 
                      className="bg-slate-800/50 rounded-lg p-4 text-center cursor-pointer hover:bg-slate-700/50 transition-colors"
                      onClick={handleAbrirModalFechasFaltas}
                      title="Ver fechas de faltas"
                    >
                      <div className="text-2xl font-bold text-red-400 font-poppins">
                        {estadisticas.entrenamiento.faltas}
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Faltas
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    <span className="text-lg font-semibold text-emerald-400 font-poppins">
                      {estadisticas.entrenamiento.porcentajeAsistencia.toFixed(1)}% de asistencia
                    </span>
                  </div>
                </div>

                {/* Estadísticas de Partidos */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-slate-200 font-poppins flex items-center">
                      <Target className="w-4 h-4 text-emerald-500 mr-2" />
                      Partidos
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-400 font-poppins">
                        {estadisticas?.titularidades?.porcentajeTitularidad?.toFixed(1) || '0.0'}%
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Titularidades
                      </div>
                      <div className="text-xs text-slate-500 font-poppins mt-1">
                        {estadisticas?.titularidades?.titularidades || 0} de {estadisticas?.titularidades?.totalJornadas || 0}
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-400 font-poppins">
                        {estadisticas?.minutos?.porcentajeMinutosTotales?.toFixed(1) || '0.0'}%
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Tiempo Total
                      </div>
                      <div className="text-xs text-slate-500 font-poppins mt-1">
                        {estadisticas?.minutos?.minutosTotal || 0} minutos
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-400 font-poppins">
                        {estadisticas?.minutos?.promedioMinutosPorPartido?.toFixed(1) || '0.0'}
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Promedio por Partido
                      </div>
                      <div className="text-xs text-slate-500 font-poppins mt-1">
                        minutos
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      {/* Botón Ver minutos usando el componente Button */}
                      <Button
                        onClick={handleAbrirModalMinutos}
                        variant="primary"
                        size="md"
                        className="mt-2"
                        title="Ver gráfica de minutos por jornada"
                      >
                        Ver minutos
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <TrendingUp className="w-16 h-16 text-slate-500 mb-4 mx-auto" />
                <p className="text-slate-400 font-poppins text-lg">
                  No hay estadísticas disponibles
                </p>
                <p className="text-slate-500 font-poppins text-sm mt-2">
                  Las estadísticas aparecerán cuando haya datos registrados
                </p>
              </div>
            )}
          </Card>

          {/* Sección 3: Registro Físico */}
          <Card variant="default" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Scale className="w-5 h-5 text-emerald-500 mr-2" />
                <h3 className="text-lg font-semibold text-slate-100 font-poppins">
                  Registro Físico
                </h3>
              </div>
              
              <Button
                variant="primary"
                size="sm"
                onClick={handleAbrirRegistroFisico}
                className="flex items-center"
              >
                Nuevo Registro
              </Button>
            </div>
            
            {ultimoRegistroFisico ? (
              <div className="space-y-4">
                <div className="text-sm text-slate-400 font-poppins mb-3">
                  Último registro: {formatearFecha(ultimoRegistroFisico.fechaRegistro)}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-400 font-poppins">
                        {ultimoRegistroFisico.altura} cm
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Altura
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-400 font-poppins">
                        {ultimoRegistroFisico.peso} kg
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        Peso
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-400 font-poppins">
                        {((ultimoRegistroFisico.peso / Math.pow(ultimoRegistroFisico.altura / 100, 2))).toFixed(1)}
                      </div>
                      <div className="text-sm text-slate-400 font-poppins mt-1">
                        IMC
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Gráfica de Evolución Física */}
                {estadisticas?.evolucionFisica && estadisticas.evolucionFisica.length > 1 ? (
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-slate-200 font-poppins mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 text-emerald-500 mr-2" />
                      Evolución Física
                    </h4>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <GraficaEvolucionFisica registros={estadisticas.evolucionFisica} />
                    </div>
                  </div>
                ) : estadisticas?.evolucionFisica && estadisticas.evolucionFisica.length > 0 && estadisticas.evolucionFisica.length <= 1 ? (
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-slate-200 font-poppins mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 text-emerald-500 mr-2" />
                      Evolución Física
                    </h4>
                    <div className="bg-secondary-800/50 rounded-lg p-4 border border-secondary-700">
                      <div className="text-center py-6">
                        <TrendingUp className="w-12 h-12 text-secondary-600 mx-auto mb-3" />
                        <p className="text-secondary-300 font-poppins text-sm">
                          Las gráficas de evolución aparecerán cuando haya más de 1 registro físico
                        </p>
                        <p className="text-secondary-400 font-poppins text-xs mt-2">
                          Registros actuales: {estadisticas.evolucionFisica.length}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="text-center py-6">
                <Scale className="w-12 h-12 text-slate-500 mb-3 mx-auto" />
                <p className="text-slate-400 font-poppins">
                  No hay registros físicos disponibles
                </p>
                <p className="text-slate-500 font-poppins text-sm mt-1">
                  Agrega el primer registro físico del jugador
                </p>
              </div>
            )}
          </Card>
        </div>
      </Modal>

      {/* Modal para agregar registro físico */}
      <ModalRegistroFisico
        isOpen={isModalRegistroFisicoOpen}
        onClose={handleCerrarRegistroFisico}
        jugadorId={jugador.id}
        onRegistroGuardado={handleRegistroFisicoGuardado}
      />

      {/* Modal para mostrar minutos por jornada */}
      <ModalMinutosJugador
        isOpen={isModalMinutosOpen}
        onClose={handleCerrarModalMinutos}
        jugadorId={jugador.id}
        jugadorNombre={jugador.nombre}
        temporadaId={temporada?.id || 0}
        temporadaNombre={temporada?.nombre || ''}
      />
      
      {/* Modal para mostrar fechas de faltas */}
      <ModalFechasFaltas
        isOpen={isModalFechasFaltasOpen}
        onClose={handleCerrarModalFechasFaltas}
        nombreJugador={jugador.nombre}
        temporadaNombre={temporada?.nombre || ''}
        fechasFaltas={fechasFaltas}
      />
    </>
  );
};

export default ModalDetalleJugador;
